import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type JSX } from 'react';
import { booksApi, type StyleDto, type UpdateBookInput } from '../api/books.js';
import { userErrorMessage } from '../api/errors.js';
import { useI18n } from '../i18n/context.js';
import { styleDisplayName, styleStoredName } from './story-styles.js';
import { SYSTEM_STYLE_PREVIEWS } from './style-previews.js';

/** The same sample pictures and single choice as Builder 2.0, using the current books API. */
export function StoryStyleField({
  label,
  style,
  disabled = false,
  onCommit,
}: {
  readonly label: string;
  readonly style: StyleDto;
  readonly disabled?: boolean;
  onCommit(input: Pick<UpdateBookInput, 'styleId' | 'styleName'>): Promise<void>;
}): JSX.Element {
  const { t } = useI18n();
  const inputId = useId();
  const queryClient = useQueryClient();
  const styles = useQuery({ queryKey: ['styles'], queryFn: booksApi.styles });
  const [describing, setDescribing] = useState(false);
  const [words, setWords] = useState('');
  const save = useMutation({
    mutationFn: onCommit,
    onSuccess: () => {
      setDescribing(false);
      void queryClient.invalidateQueries({ queryKey: ['styles'] });
    },
  });
  // Keep showing the book's style even if it has not reached the list's cache yet.
  const choices = styles.data?.items.some((item) => item.id === style.id)
    ? styles.data.items
    : [...(styles.data?.items ?? []), style];
  const selectedId = describing
    ? null
    : ((save.isPending ? save.variables.styleId : null) ?? style.id);
  const locked = disabled || save.isPending;

  return (
    <fieldset className="grid gap-3" disabled={locked}>
      <legend className="mb-3 text-lg font-semibold">{label}</legend>
      {styles.isPending ? <p>{t.common.loading}</p> : null}
      {styles.isError ? <p role="alert">{t.common.unexpectedError}</p> : null}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
        {choices.map((item) => {
          const chosen = selectedId === item.id;
          const preview = SYSTEM_STYLE_PREVIEWS[item.id];
          return (
            <button
              key={item.id}
              type="button"
              aria-pressed={chosen}
              aria-label={item.isSystem ? undefined : `${t.builder.ownStyleYours}: ${item.name}`}
              onClick={() => {
                setDescribing(false);
                save.reset();
                if (item.id !== style.id) save.mutate({ styleId: item.id });
              }}
              className={`flex flex-col overflow-hidden rounded-xl border text-left disabled:opacity-50 ${
                chosen
                  ? 'border-[var(--kitup-color-primary)] ring-2 ring-[var(--kitup-color-primary)]/40'
                  : 'border-[var(--kitup-color-border)]'
              }`}
            >
              {!item.isSystem ? (
                <span className="flex aspect-square w-full items-center overflow-hidden bg-[var(--kitup-color-surface-soft)] p-3 font-serif text-sm italic leading-snug">
                  <span className="line-clamp-6">“{item.name}”</span>
                </span>
              ) : preview ? (
                <img
                  src={preview}
                  alt=""
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
              ) : null}
              <span className="block px-2 py-1.5 text-sm font-medium">
                {item.isSystem ? styleDisplayName(item.name, t) : t.builder.ownStyleYours}
              </span>
            </button>
          );
        })}
        {disabled ? null : (
          <button
            type="button"
            aria-expanded={describing}
            aria-pressed={describing}
            aria-controls={inputId}
            onClick={() => {
              if (!words) setWords(style.isSystem ? '' : style.name);
              setDescribing(true);
              save.reset();
            }}
            className={`flex min-h-40 flex-col items-center justify-center gap-1 rounded-xl border p-3 text-center text-sm font-medium ${
              describing
                ? 'border-[var(--kitup-color-primary)] ring-2 ring-[var(--kitup-color-primary)]/40'
                : 'border-dashed border-[var(--kitup-color-border)]'
            }`}
          >
            <span aria-hidden="true" className="text-2xl leading-none">
              ✎
            </span>
            {t.builder.ownStyle}
          </button>
        )}
      </div>
      {describing ? (
        <div className="grid gap-2">
          <label htmlFor={inputId} className="text-sm font-medium">
            {t.builder.ownStyleLabel}
          </label>
          <textarea
            id={inputId}
            aria-describedby={`${inputId}-hint`}
            className="kitup-input min-h-24"
            maxLength={200}
            placeholder={t.builder.ownStylePlaceholder}
            value={words}
            onChange={(event) => setWords(event.target.value)}
          />
          <span id={`${inputId}-hint`} className="text-xs text-[var(--kitup-color-muted)]">
            {t.builder.ownStyleHint}
          </span>
          <button
            type="button"
            className="kitup-primary-button justify-self-start"
            disabled={locked || !words.trim()}
            onClick={() => save.mutate({ styleName: styleStoredName(words, t) })}
          >
            {save.isPending ? t.common.loading : t.builder.ownStyleSave}
          </button>
        </div>
      ) : null}
      {save.isError ? (
        <p role="alert" className="text-sm text-red-700">
          {userErrorMessage(save.error, t)}
        </p>
      ) : null}
    </fieldset>
  );
}
